<template>
  <!-- WRAPPER de contenido (sin footer adentro) -->
  <div class="curso-detalle-externo no-copy" @contextmenu.prevent @copy.prevent @cut.prevent @paste.prevent
    data-aos="fade-up" data-aos-duration="1200">
    
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-3">{{ translate('loading') }}</p>
    </div>

    <!-- Curso encontrado -->
    <div class="curso-detalle-wrap" v-else-if="curso.slug">

      <!-- Breadcrumbs -->
      <nav class="hero-crumbs course-crumbs hero-crumbs--light" role="navigation" aria-label="breadcrumbs">
        <router-link class="crumb-link" to="/capacitacion" :title="crumbCapacitacionLabel"
          :aria-label="crumbCapacitacionLabel">
          <svg class="crumb-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path fill="currentColor"
              d="M9.78 3.22a.75.75 0 0 1 0 1.06L6.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0z" />
          </svg>
          <span class="crumb-text">{{ crumbCapacitacionLabel }}</span>
        </router-link>
        <span class="crumb-sep" aria-hidden="true">/</span>
        <span class="crumb-current" aria-current="page">
          {{ getField(curso.nombre, selectedLang, 'courseName') }}
        </span>
      </nav>

      <!-- Header del curso -->
      <div class="curso-header d-flex flex-column flex-md-row align-items-start mb-4">
        <img data-aos="fade-left" data-aos-duration="6000" class="curso-main-img mb-3 mb-md-0 me-md-4"
          :src="curso.urlImagen" :alt="getField(curso.nombre, selectedLang, 'courseName')" />
        <div>
          <span class="curso-categoria" data-aos="fade-right" data-aos-duration="6000">
            {{ (curso.categoria[selectedLang] && curso.categoria[selectedLang][0]) || curso.categoria['es'][0] ||
              translate('category') }}
          </span>
          <h1 class="curso-title mt-1 mb-2" data-aos="fade-right" data-aos-duration="6000">
            {{ getField(curso.nombre, selectedLang, 'courseName') }}
          </h1>
          <span class="badge bg-secondary me-2" data-aos="fade-right" data-aos-duration="6000">
            {{ curso.nivel || translate('level') }}
          </span>
          <span class="badge bg-info text-dark" data-aos="fade-right" data-aos-duration="6000">
            {{ curso.tecnologia }}
          </span>
          <div class="curso-rating mt-2" data-aos="fade-right" data-aos-duration="6000">
            <span v-for="n in Math.floor(curso.rating)" :key="n" class="star">&#9733;</span>
            <span v-if="curso.rating % 1 > 0.4" class="star">&#189;</span>
            <span class="text-muted ms-2">({{ curso.rating }})</span>
          </div>
        </div>
      </div>

      <!-- Descripción -->
      <div class="text-read mb-3 fs-5" data-aos="fade-right" data-aos-duration="6000">
        {{ getField(curso.descripcion, selectedLang, 'description') }}
      </div>

      <!-- Detalles rápidos -->
      <ul class="curso-detalles-list mb-4" data-aos="fade-left" data-aos-duration="6000">
        <li data-aos="fade-left" data-aos-duration="6000">
          <strong>{{ translate('modality') }}:</strong>
          {{ (curso.modalidad && curso.modalidad.length > 1)
            ? curso.modalidad.join(' / ')
            : (curso.modalidad[0] || '') }}
        </li>
        <li data-aos="fade-right" data-aos-duration="6000">
          <strong>{{ translate('duration') }}:</strong> {{ curso.duracion }}
        </li>
        <li data-aos="fade-left" data-aos-duration="6000">
          <strong>{{ translate('schedule') }}:</strong> {{ getHorario(curso.horario) }}
        </li>
        <li data-aos="fade-right" data-aos-duration="6000">
          <strong>{{ translate('nextDate') }}:</strong> {{ curso.proximaFecha }}
        </li>
        <li data-aos="fade-right" data-aos-duration="6000">
          <strong>{{ translate('tags') }}:</strong>
          <span v-for="tag in getTags(curso.tags)" :key="tag" class="badge bg-light text-dark me-1">
            {{ tag }}
          </span>
        </li>
      </ul>

      <!-- ¿Qué aprenderás? -->
      <div v-if="curso.queAprenderas && getList(curso.queAprenderas).length" class="mb-4" data-aos="fade-up"
        data-aos-duration="1200">
        <h4 class="mb-2 text-read">{{ translate('whatYouWillLearn') }}</h4>
        <ul>
          <li v-for="item in getList(curso.queAprenderas)" :key="item">{{ item }}</li>
        </ul>
      </div>

      <!-- Temario -->
      <div v-if="curso.contenido && getContenidoList(curso.contenido).length" class="mb-4" data-aos="fade-up"
        data-aos-duration="1200">
        <h4 class="mb-2 text-read">{{ getFirstHeading(curso.contenido) }}</h4>
        <ul>
          <li v-for="item in getContenidoList(curso.contenido)" :key="item">{{ item }}</li>
        </ul>
      </div>

      <!-- Requisitos -->
      <div v-if="curso.contenido && getRequisitos(curso.contenido).length" class="mb-4" data-aos="fade-up"
        data-aos-duration="1200">
        <h5 class="mb-2 text-read">{{ getRequisitosHeading(curso.contenido) }}</h5>
        <ul>
          <li v-for="item in getRequisitos(curso.contenido)" :key="item">{{ item }}</li>
        </ul>
      </div>

      <!-- Incluye -->
      <div v-if="curso.incluye && getList(curso.incluye).length" class="mb-4" data-aos="fade-up"
        data-aos-duration="1200">
        <h5 class="mb-2 text-read">{{ translate('includes') }}</h5>
        <ul>
          <li v-for="item in getList(curso.incluye)" :key="item">{{ item }}</li>
        </ul>
      </div>

      <!-- Botón flotante WhatsApp -->
      <button class="whatsapp-fab" @click="onWhatsappClick" aria-label="Inscribirme por WhatsApp"
        title="Inscribirme por WhatsApp">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="currentColor"
          aria-hidden="true">
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.031-.967-.273-.1-.473-.149-.673.15-.197.297-.77.966-.944 1.164-.173.199-.347.224-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.173.198-.298.298-.497.1-.198.05-.372-.025-.521-.075-.149-.673-1.61-.922-2.206-.242-.579-.487-.5-.673-.51-.173-.009-.372-.011-.572-.011-.198 0-.52.074-.792.372-.273.298-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.571-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.275c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.987c-.003 5.45-4.437 9.884-9.886 9.884M20.52 3.45A11.815 11.815 0 0 0 11.93 0C5.347 0 .103 5.243.1 11.826c0 2.086.547 4.124 1.588 5.927L0 24l6.41-1.682a11.82 11.82 0 0 0 5.61 1.428h.005c6.58 0 11.925-5.343 11.928-11.923a11.822 11.822 0 0 0-3.433-8.373z" />
        </svg>
        <span class="whatsapp-fab-label">WhatsApp</span>
      </button>
      <div v-if="showToast" class="curso-toast">
        Abriendo WhatsApp...
      </div>
    </div>

    <!-- Curso no encontrado -->
    <div v-else class="text-center py-5">
      <p>{{ translate('courseNotFound') }}</p>
      <router-link class="btn btn-primary mt-3" to="/capacitacion">← {{ translate('goBack') }}</router-link>
    </div>
  </div>

  <!-- FOOTER fuera del wrapper para evitar conflictos -->
  <SiteFooter :selectedLang="selectedLang" />
</template>

<script>
import { useSEO } from '@/composables/useSEO';
import { database, ref as dbRef, get } from '@/firebase.js'
import SiteFooter from '@/components/SiteFooter.vue'
import AOS from 'aos'
import 'aos/dist/aos.css'

const TRANSLATIONS = {
  loading: { es: "Cargando curso...", en: "Loading course...", de: "Kurs wird geladen..." },
  courseNotFound: { es: "Curso no encontrado.", en: "Course not found.", de: "Kurs nicht gefunden." },
  goBack: { es: "Volver al listado", en: "Back to listing", de: "Zurück zur Übersicht" },
  category: { es: "Categoría", en: "Category", de: "Kategorie" },
  level: { es: "Nivel", en: "Level", de: "Niveau" },
  modality: { es: "Modalidad", en: "Modality", de: "Modalität" },
  duration: { es: "Duración", en: "Duration", de: "Dauer" },
  schedule: { es: "Horario", en: "Schedule", de: "Zeitplan" },
  nextDate: { es: "Próxima fecha", en: "Next date", de: "Nächstes Datum" },
  cost: { es: "Costo", en: "Cost", de: "Kosten" },
  tags: { es: "Etiquetas", en: "Tags", de: "Tags" },
  whatYouWillLearn: { es: "¿Qué aprenderás?", en: "What you will learn", de: "Was du lernen wirst" },
  includes: { es: "Incluye", en: "Includes", de: "Enthält" }
}

export default {
  name: 'CursoDetalle',
  components: { SiteFooter },
  props: { selectedLang: { type: String, default: 'es' } },
  setup() {
    const seoData = {
      title: 'Detalle del Curso',
      description: 'Curso especializado de capacitación industrial en PLC, SCADA, TIA Portal, Node-RED, Grafana e Industria 4.0 con Frenkel Engineering.',
      path: '/capacitacion',
    };
    useSEO(seoData);
    return { seoData };
  },
  data() {
    return {
      curso: {},
      isLoading: true,
      showToast: false
    }
  },
  computed: {
    whatsappLink() {
      const nombreCurso = this.getField(this.curso.nombre, this.selectedLang, 'courseName')
      const texto = encodeURIComponent(`¡Hola! Quiero inscribirme al curso: ${nombreCurso}`)
      return `https://wa.me/5539960462?text=${texto}`
    },
    crumbCapacitacionLabel() {
      const map = { es: 'Capacitación', en: 'Training', de: 'Schulungen' }
      return map[this.selectedLang] || map.es
    }
  },
  async created() {
    await this.loadCurso()
  },
  methods: {
    async loadCurso() {
      try {
        this.isLoading = true
        const slug = this.$route.params.slug
        
        // Obtener todos los cursos de Firebase
        const cursosRef = dbRef(database, 'cursos')
        const snapshot = await get(cursosRef)
        
        if (snapshot.exists()) {
          const data = snapshot.val()
          
          // Convertir a array si es necesario
          const cursosArray = Array.isArray(data)
            ? data.filter(item => item !== null)
            : Object.keys(data).map(key => ({ firebaseId: key, ...data[key] }))
          
          // Buscar el curso por slug
          const encontrado = cursosArray.find(c => c.slug === slug)
          
          if (encontrado) {
            this.curso = encontrado
            // Actualizar SEO dinámico con datos del curso
            const nombre = this.getField(encontrado.nombre, this.selectedLang, 'courseName')
            const descripcion = this.getField(encontrado.descripcion, this.selectedLang, 'description')
            this.seoData.title = `${nombre} | Capacitación Frenkel`
            this.seoData.description = descripcion.substring(0, 160)
            this.seoData.path = `/capacitacion/${slug}`
            console.log('Curso cargado:', encontrado.nombre)
          } else {
            console.log('Curso no encontrado con slug:', slug)
            this.$router.replace('/error-404')
          }
        } else {
          console.log('No hay cursos en Firebase')
          this.$router.replace('/error-404')
        }
      } catch (error) {
        console.error('Error al cargar curso:', error)
        this.$router.replace('/error-404')
      } finally {
        this.isLoading = false
      }
    },
    
    translate(key) {
      return TRANSLATIONS[key]?.[this.selectedLang] || TRANSLATIONS[key]?.es || key
    },
    
    getField(field, lang, fallbackKey) {
      if (!field) return this.translate(fallbackKey)
      if (typeof field === 'object') return field[lang] || field['es'] || this.translate(fallbackKey)
      return field
    },
    
    getList(listObj) {
      if (!listObj) return []
      return listObj[this.selectedLang] || listObj['es'] || []
    },
    
    getTags(tagsObj) {
      if (!tagsObj) return []
      return tagsObj[this.selectedLang] || tagsObj['es'] || []
    },
    
    getHorario(horarioObj) {
      if (!horarioObj) return ''
      return horarioObj[this.selectedLang] || horarioObj['es'] || ''
    },
    
    getFirstHeading(contenidoObj) {
      if (!contenidoObj) return ''
      const arr = contenidoObj[this.selectedLang] || contenidoObj['es'] || []
      const h = arr.find(item => item.type === 'heading')
      return h ? h.text : ''
    },
    
    getContenidoList(contenidoObj) {
      if (!contenidoObj) return []
      const arr = contenidoObj[this.selectedLang] || contenidoObj['es'] || []
      return arr.filter(item => item.type === 'list').map(i => i.text)
    },
    
    getRequisitos(contenidoObj) {
      if (!contenidoObj) return []
      const arr = contenidoObj[this.selectedLang] || contenidoObj['es'] || []
      let reqStart = arr.findIndex(item => item.type === 'heading' && item.text.toLowerCase().includes('requisito'))
      if (reqStart < 0) return []
      const out = []
      for (let i = reqStart + 1; i < arr.length && arr[i].type === 'list'; i++) out.push(arr[i].text)
      return out
    },
    
    getRequisitosHeading(contenidoObj) {
      if (!contenidoObj) return ''
      const arr = contenidoObj[this.selectedLang] || contenidoObj['es'] || []
      const h = arr.find(item => item.type === 'heading' && item.text.toLowerCase().includes('requisito'))
      return h ? h.text : ''
    },
    
    formatCurrency(costo) {
      if (!costo) return ''
      const symbol = costo.moneda === 'USD' ? 'USD$' : '$'
      const num = costo.cantidad?.toLocaleString(this.selectedLang, { minimumFractionDigits: 0 })
      return symbol + ' ' + num
    },
    
    onWhatsappClick() {
      this.showToast = true
      window.open(this.whatsappLink, '_blank', 'noopener')
      setTimeout(() => { this.showToast = false }, 2500)
    }
  },
  
  mounted() {
    AOS.init({ duration: 800, once: true })
    setTimeout(() => AOS.refresh(), 300)
  }
}
</script>

<style scoped>
/* Wrapper sin flex/min-height para no "pegarse" con el footer */
.curso-detalle-externo {
  background: #f6fafb;
  padding: 24px 0 32px;
  /* margen inferior propio, footer va fuera */
  overflow-x: hidden;
}

.curso-detalle-wrap {
  background: #fff;
  max-width: 970px;
  margin: 0 auto 48px;
  padding: 28px 14px 10px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(30, 60, 100, 0.12);
}

/* Breadcrumbs */
.hero-crumbs {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  line-height: 1;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.hero-crumbs--light {
  background: #f5f7fb;
  border: 1px solid #e4e8f0;
  color: #0b2342;
}

.course-crumbs {
  margin-bottom: 12px;
}

.crumb-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: inherit;
  transition: opacity .2s, transform .2s;
}

.crumb-link:hover {
  opacity: .9;
  transform: translateX(-1px);
}

.crumb-link:focus {
  outline: 2px solid #0b2342;
  outline-offset: 2px;
}

.crumb-icon {
  display: inline-block;
}

.crumb-sep {
  opacity: .65;
  margin: 0 2px;
}

.crumb-current {
  opacity: .9;
  font-weight: 600;
}

/* Header */
.curso-header {
  border-bottom: 2px solid #e5eef6;
  padding-bottom: 18px;
  margin-bottom: 22px;
}

.curso-main-img {
  width: 260px;
  height: 175px;
  object-fit: cover;
  border-radius: 14px;
  box-shadow: 0 3px 16px 0 rgba(30, 60, 120, 0.09);
  background: #e5e7eb;
  transition: background-color .3s, transform .3s, box-shadow .3s;
}

.curso-main-img:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 13px 0 rgba(100, 100, 120, .08);
}

.curso-title {
  font-family: 'Caveat-Bold', cursive;
  font-size: 2.1rem;
  font-weight: 700;
  color: #123c5e;
}

.curso-categoria {
  font-size: .98rem;
  font-weight: 600;
  color: #2478c4;
  background: #f2f7fc;
  border-radius: 7px;
  padding: 3px 13px;
  display: inline-block;
  margin-bottom: 6px;
}

.curso-rating .star {
  color: #ffe066;
  font-size: 1.25rem;
  transition: transform .3s, box-shadow .3s;
}

.curso-rating .star:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 13px 0 rgba(100, 100, 120, .08);
}

/* Detalles */
.curso-detalles-list {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 1rem;
  color: #274158;
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px 24px 10px;
  list-style: none;
}

.curso-detalles-list li {
  margin-bottom: 8px;
}

.badge {
  font-size: .92rem;
  margin-bottom: 2px;
  vertical-align: middle;
}

/* Botón WhatsApp */
.whatsapp-fab {
  position: fixed;
  right: 24px;
  bottom: 32px;
  z-index: 1100;
  display: flex;
  align-items: center;
  background: #22c55e;
  border: none;
  border-radius: 50px;
  box-shadow: 0 4px 18px 0 rgba(40, 200, 120, .17);
  padding: 10px 18px 10px 13px;
  color: #fff;
  font-size: 1.08rem;
  font-weight: 700;
  cursor: pointer;
  transition: background .18s, box-shadow .18s;
}

.whatsapp-fab:hover {
  background: #18a34b;
  box-shadow: 0 7px 24px 0 rgba(30, 170, 80, .21);
}

.whatsapp-fab-label {
  margin-left: 10px;
  font-family: 'Space Grotesk', 'Caveat-Bold', cursive;
  font-size: 1.04rem;
}

.curso-toast {
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  background: #30e87c;
  color: #085c28;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 2px 8px 0 rgba(30, 120, 60, .13);
  padding: 10px 32px;
  z-index: 1050;
}

/* Subrayado bonito */
.text-read {
  position: relative;
  transition: color .3s;
  display: inline-block;
}

.text-read::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 0;
  background-color: currentColor;
  transition: width .3s;
}

.text-read:hover::after {
  width: 100%;
}

/* Spinner */
.spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3em;
}

/* Responsive */
@media (max-width: 768px) {
  .curso-main-img {
    width: 100%;
    height: 120px;
    margin-bottom: 12px;
  }

  .curso-title {
    font-size: 1.25rem;
  }

  .curso-detalle-wrap {
    padding: 11px 1vw;
  }

  .course-crumbs {
    font-size: 0.9rem;
    padding: 5px 10px;
  }
}

@media (max-width: 600px) {
  .whatsapp-fab {
    right: 14px;
    bottom: 14px;
    font-size: .97rem;
    padding: 9px 13px 9px 10px;
  }

  .whatsapp-fab-label {
    display: none;
  }

  .curso-title {
    font-size: 1.12rem;
  }

  .curso-main-img {
    height: 80px;
  }
}

/* No copiar */
.no-copy {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>